import { useEffect, useState } from 'react';
import { axiosInstance } from 'utils/axios';

// material-ui
import { Box, Button, Grid, Typography, TextField, Select, MenuItem, Autocomplete, CircularProgress, Checkbox } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { deepPurple } from '@mui/material/colors';
import { gridSpacing } from 'app/constant';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentToken } from 'features/auth/authSlice';
import StickyHeadTable from './components/StickyHeadTable';
import { warningAlert, successAlert, errorAlert } from 'ui-component/Toasts';
import useFetchData from 'hooks/useFetchData';
import InsertDepositRecord from './components/InsertDepositRecords';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from "react-i18next";

const Deposit = () => {
    const { t } = useTranslation();
    const user = useSelector(selectCurrentUser);
    const token = useSelector(selectCurrentToken);
    const theme = useTheme();

    const [deposit, setdeposit] = useState([]);
    const [clientName, setclientName] = useState('');
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'days'));
    const [dateTo, setDateTo] = useState(dayjs());
    const [filterApproveDateFrom, setFilterApproveDateFrom] = useState('');
    const [filterApproveDateTo, setFilterApproveDateTo] = useState('');
    const [customerLoginUserName, setCustomerLoginUserName] = useState('');
    const [status, setstatus] = useState('');
    const [thirdPartyID, setThirdPartyID] = useState('');
    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [transactionNumber, setTransactionNumber] = useState('');
    const [isTestMember, setIsTestMember] = useState('');
    const [numberCode, setNumberCode] = useState('');

    const fetchdepositRecord = async () => {
        let depositRecordURL = `/Deposit/All?dateFrom=${dateFrom.format('YYYY-MM-DD')}&dateTo=${dateTo.format('YYYY-MM-DD')}`;
        if (filterApproveDateFrom !== '' && filterApproveDateFrom !== null) {
            depositRecordURL += `&approvedDateFrom=${filterApproveDateFrom.format('YYYY-MM-DD')}`;
        }

        if (filterApproveDateTo !== '' && filterApproveDateTo !== null) {
            depositRecordURL += `&approvedDateTo=${filterApproveDateTo.format('YYYY-MM-DD')}`;
        }

        if (customerLoginUserName !== '') {
            depositRecordURL += `&customerLoginUserName=${customerLoginUserName}`;
        }

        if (numberCode !== '') {
            depositRecordURL += `&numberCode=${numberCode}`;
        }

        if (status !== 0) {
            depositRecordURL += `&statusID=${status}`;
        }

        if (thirdPartyID !== '') {
            depositRecordURL += `&thirdPartyID=${thirdPartyID}`;
        }

        if (transactionNumber !== '') {
            depositRecordURL += `&transactionNumber=${transactionNumber}`;
        }

        if (isTestMember !== '') {
            depositRecordURL += `&isTestMember=${isTestMember}`;
        }

        setLoading(true);
        setFilterBtnDisabled(true);
        try {
            const res = await axiosInstance.get(depositRecordURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setdeposit(res.data);
                console.log(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
            setFilterBtnDisabled(false);
        }
    };

    const [exportDataListLoading, setExportDataListLoading] = useState(false);
    function convertDataForSheet(data) {
        if (!data || data.length === 0) {
            warningAlert('There is no data to export');
            throw new Error('Data array is empty or undefined.');
        }

        const headers = Object.keys(data[0]);
        const dataArray = [headers];

        data.forEach((item) => {
            const row = headers.map((key) => item[key]);
            dataArray.push(row);
        });

        return XLSX.utils.aoa_to_sheet(dataArray);
    }
    const exportDataAsXlsx = async () => {
        setFilterBtnDisabled(true);
        setTimeout(() => {
            setFilterBtnDisabled(false);
        }, 3000);
        setExportDataListLoading(true);
        let depositRecordURL = `/Deposit/All?dateFrom=${dateFrom.format('YYYY-MM-DD')}&dateTo=${dateTo.format('YYYY-MM-DD')}`;
        if (filterApproveDateFrom !== '' && filterApproveDateFrom !== null) {
            depositRecordURL += `&approvedDateFrom=${filterApproveDateFrom.format('YYYY-MM-DD')}`;
        }

        if (filterApproveDateTo !== '' && filterApproveDateTo !== null) {
            depositRecordURL += `&approvedDateTo=${filterApproveDateTo.format('YYYY-MM-DD')}`;
        }
        if (customerLoginUserName !== '') {
            depositRecordURL += `&customerLoginUserName=${customerLoginUserName}`;
        }

        if (status !== 0) {
            depositRecordURL += `&statusID=${status}`;
        }

        if (thirdPartyID !== '') {
            depositRecordURL += `&thirdPartyID=${thirdPartyID}`;
        }

        if (transactionNumber !== '') {
            depositRecordURL += `&transactionNumber=${transactionNumber}`;
        }

        if (isTestMember !== '') {
            depositRecordURL += `&isTestMember=${isTestMember}`;
        }

        try {
            const res = await axiosInstance.get(depositRecordURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                const fileName = `Deposit_${dayjs().format('MM-DD-YYYY')}`;
                const workbook = XLSX.utils.book_new();
                const worksheet = convertDataForSheet(res.data);
                // Adjust column widths
                const headers = Object.keys(res.data[0]);
                const colWidths = headers.map((header) => ({ wch: header.length + 2 }));

                worksheet['!cols'] = colWidths;

                XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
                XLSX.writeFile(workbook, `${fileName}.xlsx`);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setExportDataListLoading(false);
        }
    };

    // const { data: ClientNames } = useFetchData('/Customer/Names');
    const { data: CustomerLoginUserNames } = useFetchData('/Customer/LoginUserNames');
    const { data: DepositStatus } = useFetchData('/DepositStatus/All');
    const { data: thirdPartyIDs } = useFetchData('/ThirdParty');

    const [isUserAllowEdit, setIsUserAllowEdit] = useState(false);
    const fetchIsUserAllowEdit = async () => {
        try {
            const res = await axiosInstance.get('IsAllowEdit', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setIsUserAllowEdit(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        Promise.all([fetchdepositRecord(), fetchIsUserAllowEdit()]).catch((error) => {
            console.error('An error occurred:', error);
        });
    }, []);

    return (
        <Grid container item xs={12} padding="20px">
            {isUserAllowEdit &&
                <InsertDepositRecord customerLoginUserNames={CustomerLoginUserNames} status={DepositStatus} thirdPartyIDs={thirdPartyIDs} fetchdepositRecord={fetchdepositRecord} />
            }
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>{t("withdrawal.createdDate")} :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dateFrom}
                                slotProps={{
                                    textField: {
                                        error: false,
                                        size: 'small', // makes the input smaller (MUI supports 'small' and 'medium')
                                        sx: {
                                          '& .MuiInputBase-root': {
                                            fontSize: '13px',      // font size of the input
                                            padding: '4px 8px',    // optional: reduce input padding
                                            minHeight: '36px',     // optional: control input height
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '13px',      // font size inside input
                                            padding: '8.5px 14px', // adjust input padding if needed
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '13px',      // label font size
                                          },
                                        },
                                    }
                                }}
                                onChange={(newValue) => setDateFrom(newValue)}
                            />
                        </LocalizationProvider>
                        -
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dateTo}
                                slotProps={{
                                    textField: {
                                        error: false,
                                        size: 'small', // makes the input smaller (MUI supports 'small' and 'medium')
                                        sx: {
                                          '& .MuiInputBase-root': {
                                            fontSize: '13px',      // font size of the input
                                            padding: '4px 8px',    // optional: reduce input padding
                                            minHeight: '36px',     // optional: control input height
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '13px',      // font size inside input
                                            padding: '8.5px 14px', // adjust input padding if needed
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '13px',      // label font size
                                          },
                                        },
                                    }
                                }}
                                onChange={(newValue) => setDateTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("withdrawal.loginUserName")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={customerLoginUserName}
                            onChange={(e) => setCustomerLoginUserName(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("withdrawal.code")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={numberCode}
                            onChange={(e) => setNumberCode(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("deposit.depositStatus")} :
                        </Typography>
                        <Select
                            displayEmpty
                            value={status}
                            onChange={(e) => setstatus(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '36px',
                                '& .MuiSelect-select': {
                                    fontSize: '13px',
                                    padding: '10px 14px',
                                },
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '36px',
                                    padding: '10px 14px',
                                },
                            }}
                        >
                            <MenuItem sx={{ width: '100%' }} value="">
                                {t("deposit.pleaseSelectDepositStatus")}
                            </MenuItem>
                            {DepositStatus.map((item, index) => (
                                <MenuItem sx={{ width: '100%' }} value={item.id} key={index}>
                                    {item.id === 1 ? t("withdrawal.pending") : item.id === 2 ? t("withdrawal.approved") : item.id === 3 ? t("withdrawal.rejected") : ''}
                                    {/* {item.name} */}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={12} lg={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            gap: '15px',
                            width: '50%',
                            marginLeft: 'auto'
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{
                                float: 'right',
                                display: 'block',
                                mx: 'auto',
                                width: '150px',
                                backgroundColor: deepPurple[500],
                                marginTop: '20px',
                                marginBottom: '30px'
                            }}
                            onClick={() => fetchdepositRecord()}
                            disabled={filterBtnDisabled}
                        >
                            {t("withdrawal.filter")}
                        </Button>
                    </Box>
                </Grid>
                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable
                            deposit={deposit}
                            fetchdepositRecord={fetchdepositRecord}
                            customerLoginUserNames={CustomerLoginUserNames}
                            status={DepositStatus}
                            thirdPartyIDs={thirdPartyIDs}
                        />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default Deposit;
